import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { DataProvider } from "./DataContext";
import { canAccess } from "./auth";

// Pages
import Login               from "./pages/Login";
import PortfolioOverview   from "./pages/PortfolioOverview";
import ProjectHealth       from "./pages/ProjectHealth";
import BenefitsRealization from "./pages/BenefitsRealization";
import RiskIntelligence    from "./pages/RiskIntelligence";
import ResourceIntelligence from "./pages/ResourceIntelligence";
import FinancialPerformance from "./pages/FinancialPerformance";
import ScheduleCriticalPath from "./pages/ScheduleCriticalPath";
import InterdependencyRisk  from "./pages/InterdependencyRisk";
import PMWorkforceQuality   from "./pages/PMWorkforceQuality";
import PortfolioRationalization from "./pages/PortfolioRationalization";
import ExecutiveBoardView   from "./pages/ExecutiveBoardView";
import AIAgentConsole       from "./pages/AIAgentConsole";
import UserManagement       from "./pages/UserManagement";
import Settings             from "./pages/Settings";
import PortfolioRegistry    from "./pages/PortfolioRegistry";
import ReportsCenter        from "./pages/ReportsCenter";
import PlatformMap          from "./pages/PlatformMap";

const SESSION_HOURS = 8;

function PrivateRoute({ children, path }) {
  const raw = sessionStorage.getItem("pmo_user");
  if (!raw) return <Navigate to="/login" replace />;

  try {
    const user    = JSON.parse(raw);
    const loginAt = new Date(user.loginAt);
    const expired = (Date.now() - loginAt.getTime()) > SESSION_HOURS * 3600 * 1000;

    if (expired) {
      sessionStorage.removeItem("pmo_user");
      return <Navigate to="/login" replace />;
    }

    if (path && !canAccess(user.role, path)) {
      return <Navigate to="/portfolio" replace />;
    }

    return children;
  } catch {
    sessionStorage.removeItem("pmo_user");
    return <Navigate to="/login" replace />;
  }
}

function PR({ path, children }) {
  return <PrivateRoute path={path}>{children}</PrivateRoute>;
}

export default function App() {
  return (
    <DataProvider>
      <BrowserRouter basename="/pmo">
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Redirect root to portfolio */}
          <Route path="/" element={<Navigate to="/portfolio" replace />} />

          {/* P-01 through P-12 */}
          <Route path="/portfolio"      element={<PR path="/portfolio"><PortfolioOverview /></PR>} />
          <Route path="/health"         element={<PR path="/health"><ProjectHealth /></PR>} />
          <Route path="/benefits"       element={<PR path="/benefits"><BenefitsRealization /></PR>} />
          <Route path="/risk"           element={<PR path="/risk"><RiskIntelligence /></PR>} />
          <Route path="/resources"      element={<PR path="/resources"><ResourceIntelligence /></PR>} />
          <Route path="/financial"      element={<PR path="/financial"><FinancialPerformance /></PR>} />
          <Route path="/schedule"       element={<PR path="/schedule"><ScheduleCriticalPath /></PR>} />
          <Route path="/dependencies"   element={<PR path="/dependencies"><InterdependencyRisk /></PR>} />
          <Route path="/workforce"      element={<PR path="/workforce"><PMWorkforceQuality /></PR>} />
          <Route path="/rationalization" element={<PR path="/rationalization"><PortfolioRationalization /></PR>} />
          <Route path="/executive"      element={<PR path="/executive"><ExecutiveBoardView /></PR>} />
          <Route path="/ai-agent"       element={<PR path="/ai-agent"><AIAgentConsole /></PR>} />

          {/* P-00, P-13, P-14 */}
          <Route path="/platform-map" element={<PR path="/platform-map"><PlatformMap /></PR>} />
          <Route path="/registry"     element={<PR path="/registry"><PortfolioRegistry /></PR>} />
          <Route path="/reports"      element={<PR path="/reports"><ReportsCenter /></PR>} />

          {/* Admin */}
          <Route path="/users"    element={<PR path="/users"><UserManagement /></PR>} />
          <Route path="/settings" element={<PR path="/settings"><Settings /></PR>} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/portfolio" replace />} />
        </Routes>
      </BrowserRouter>
    </DataProvider>
  );
}
